(function(){
  "use strict";
  class AlbuminDoseCalculator extends HTMLElement{
    connectedCallback(){
      if(this.__albuminDoseInitialized) return;
      this.__albuminDoseInitialized = true;
      var root = this;

      /* ---------- constants ---------- */
      var LB_PER_KG = 2.20462;
      var DIST = 0.3;          // L/kg albumin space in the classic deficit formula
      var EARLY_LO = 2.5;      // g/kg per 1 g/dL early rise, low end (about +0.4 g/dL per 1 g/kg)
      var EARLY_HI = 1.25;     // g/kg per 1 g/dL early rise, high end (about +0.8 g/dL per 1 g/kg)
                               // Healthy dogs given 1 g/kg of 16% canine albumin rose a mean of 0.48 g/dL (range 0.2 to 0.8) at 2 h (Enders 2024)
      var PLASMA_ALB = 0.03;   // g/mL albumin in plasma (about 3 g/dL)
      var CAP_GKG = 2;         // g/kg per day
      var HSA_CRI_MAX = 1.7;   // mL/kg/hr, 25% HSA published CRI upper limit
      var CSA_MLMIN = 1;       // mL/min, canine albumin insert suggested maximum (normovolaemic)
      var VOL_WARN = 20;       // mL/kg total product volume
      var OVERSHOOT = 3.0;     // g/dL

      var PRODUCTS = {
        h25:{ name:"Human albumin 25%", short:"HSA 25%", gml:0.25, human:true,  canine:false, dur:4, bottle:50 },
        h20:{ name:"Human albumin 20%", short:"HSA 20%", gml:0.20, human:true,  canine:false, dur:4, bottle:100 },
        h5: { name:"Human albumin 5%",  short:"HSA 5%",  gml:0.05, human:true,  canine:false, dur:4, bottle:250 },
        h4: { name:"Human albumin 4%",  short:"HSA 4%",  gml:0.04, human:true,  canine:false, dur:4, bottle:500 },
        c16:{ name:"Canine albumin 16%", short:"canine albumin 16%", gml:0.16, human:false, canine:true, dur:6, vialG:5, diluent:30 },
        c5: { name:"Canine albumin 5%",  short:"canine albumin 5%",  gml:0.05, human:false, canine:true, dur:6, vialG:5, diluent:100 },
        custom:{ name:"Custom albumin", short:"custom albumin", gml:0.10, human:true, canine:false, dur:4, bottle:100 }
      };

      var state = {
        species: root.dataset.defaultSpecies === "cat" ? "cat" : "dog",
        wunit: "kg",
        unit: root.dataset.defaultUnit === "gl" ? "gl" : "gdl",
        mode: "deficit",
        product: PRODUCTS[root.dataset.defaultProduct] ? root.dataset.defaultProduct : "h25",
        csrc: "human",
        prior: "no",
        volsens: "no",
        cap: "on"
      };

      var $ = function(id){ return root.querySelector("#" + id); };

      /* ---------- helpers ---------- */
      function fmt(n, d){ return isFinite(n) ? n.toFixed(d === undefined ? 2 : d) : "–"; }
      function fmtSmart(n){ // volumes: 2 dp under 10, 1 dp under 100, whole above
        if(!isFinite(n)) return "–";
        var a = Math.abs(n);
        return a < 10 ? n.toFixed(2) : a < 100 ? n.toFixed(1) : n.toFixed(0);
      }
      function fmtX(n){ return Math.abs(n - Math.round(n)) < 0.05 ? n.toFixed(0) : n.toFixed(1); }
      function albDisplay(gdl){ // "1.5 g/dL · 15 g/L"
        return fmt(gdl,1) + " g/dL · " + fmt(gdl*10,0) + " g/L";
      }
      function inUnit(gdl){ return state.unit === "gdl" ? fmt(gdl,1) + " g/dL" : fmt(gdl*10,0) + " g/L"; }
      function riseInUnit(gdl){ return state.unit === "gdl" ? fmt(gdl,2) + " g/dL" : fmt(gdl*10,1) + " g/L"; }

      function product(){
        var p = PRODUCTS[state.product];
        if(state.product === "custom"){
          var pct = parseFloat($("cpct").value);
          p = Object.assign({}, p, { gml: isFinite(pct) && pct > 0 ? pct/100 : NaN,
                                    human: state.csrc === "human", canine: state.csrc === "canine", vialG: 5,
                                    name: (state.csrc === "canine" ? "Custom canine albumin" : "Custom human albumin") + (isFinite(pct) ? " " + fmt(pct, pct % 1 ? 1 : 0) + "%" : ""),
                                    short: "custom albumin" });
        }
        return p;
      }

      /* ---------- segmented controls ---------- */
      function seg(groupId, key, after){
        var g = $(groupId);
        g.querySelectorAll("button[data-v]").forEach(function(b){
          b.setAttribute("aria-pressed", b.classList.contains("on") ? "true" : "false");
          b.addEventListener("click", function(){
            g.querySelectorAll("button").forEach(function(x){ x.classList.remove("on"); x.setAttribute("aria-pressed","false"); });
            b.classList.add("on"); b.setAttribute("aria-pressed","true");
            var old = state[key];
            state[key] = b.dataset.v;
            if(after) after(old, state[key]);
            calc();
          });
        });
      }
      function setSeg(groupId, v){
        $(groupId).querySelectorAll("button[data-v]").forEach(function(x){
          var on = x.dataset.v === v; x.classList.toggle("on", on); x.setAttribute("aria-pressed", on ? "true" : "false");
        });
      }

      seg("species", "species");
      seg("wunit", "wunit", function(from, to){
        var el = $("wt"), v = parseFloat(el.value);
        if(isNaN(v)) return;
        if(from === "kg" && to === "lb") el.value = (v * LB_PER_KG).toFixed(1);
        else if(from === "lb" && to === "kg") el.value = (v / LB_PER_KG).toFixed(1);
      });
      seg("unit", "unit", function(from, to){
        ["alb","target"].forEach(function(id){
          var el = $(id), v = parseFloat(el.value);
          if(isNaN(v)) return;
          if(from === "gdl" && to === "gl") el.value = String(parseFloat((v * 10).toFixed(1)));
          else if(from === "gl" && to === "gdl") el.value = String(parseFloat((v / 10).toFixed(2)));
        });
        $("target").step = to === "gl" ? "1" : "0.1";
        $("alb").step = to === "gl" ? "0.5" : "0.1";
        $("targetUnit").textContent = to === "gl" ? "g/L" : "g/dL";
      });
      seg("mode", "mode", function(){ syncMode(); });
      seg("csrc", "csrc", function(){ syncProduct(false); });
      seg("prior", "prior");
      seg("volsens", "volsens");
      seg("cap", "cap");

      function syncMode(){
        $("targetField").hidden = state.mode !== "deficit";
        $("gkgField").hidden = state.mode !== "gkg";
        $("rowDeficit").hidden = state.mode !== "deficit";
      }

      /* ---------- product change ---------- */
      var dirty = { dur:false, bottle:false };
      function syncProduct(setDefaults){
        var p = product();
        $("customWrap").hidden = state.product !== "custom";
        $("bottleField").hidden = !!p.canine;
        if(setDefaults){
          if(!dirty.dur) $("dur").value = p.dur;
          if(!p.canine && !dirty.bottle) $("bottle").value = p.bottle;
        }
        $("durHint").textContent = p.canine
          ? "Canine albumin: 450 to 800 mg/kg is usually run over 4 to 8 h (about 6 h in the septic peritonitis study). Slower for normovolaemic patients."
          : (p.gml >= 0.16
              ? "Hyperoncotic solutions: 2 mL/kg of 25% over 2 h for hypotension, or a CRI of 0.7 to 1.7 mL/kg/hr. Longer (12 to 24 h) if volume-sensitive."
              : "Iso-oncotic solutions carry the same albumin in 5 to 6 times the volume; give over 4 to 6 h or longer, watching for overload.");
      }
      $("product").addEventListener("change", function(){
        state.product = $("product").value;
        syncProduct(true);
        calc();
      });

      /* ---------- inputs ---------- */
      ["wt","alb","target","gkg","cpct","dur","bottle"].forEach(function(id){
        $(id).addEventListener("input", function(){
          if(id === "dur") dirty.dur = $("dur").value !== "";
          if(id === "bottle") dirty.bottle = $("bottle").value !== "";
          calc();
        });
      });

      /* ---------- interpretation ---------- */
      function interpret(gdl, species){
        if(isNaN(gdl)) return null;
        var ref = species === "cat"
          ? "Typical feline reference ≈ 2.4 to 4.0 g/dL (24 to 40 g/L). Use your laboratory's interval. Evidence for albumin products in cats is limited; see alerts."
          : "Typical canine reference ≈ 2.5 to 4.0 g/dL (25 to 40 g/L). Use your laboratory's interval.";
        var lead = "Albumin " + albDisplay(gdl);
        if(gdl >= 2.5) return { cls:"ok", badge:"Within or near range", badgeCls:"b-ok", ref:ref, lead:lead,
          desc:"Oncotic pressure is usually adequate at this level. Albumin products are rarely indicated: investigate and treat the cause and trend the value." };
        if(gdl >= 2.0) return { cls:"warn", badge:"Mild hypoalbuminaemia", badgeCls:"b-warn", ref:ref, lead:lead,
          desc:"COP is falling but oedema is uncommon above 2.0 g/dL. Supplement only with hypotension, oedema or effusion, or heavy ongoing losses; otherwise treat the cause and recheck." };
        if(gdl >= 1.5) return { cls:"warn", badge:"Moderate hypoalbuminaemia", badgeCls:"b-warn", ref:ref, lead:lead,
          desc:"Below 2.0 g/dL interstitial oedema, effusions, poor wound healing and altered drug binding become likely. Oncotic support is reasonable with clinical signs, hypotension, or a surgical patient with ongoing losses." };
        return { cls:"dang", badge:"Severe hypoalbuminaemia", badgeCls:"b-dang", ref:ref, lead:lead,
          desc:"COP is critically low. Oncotic support with albumin is usually indicated alongside treatment of the cause. Give it slowly, recheck albumin, TP and COP, and watch for volume overload." };
      }

      /* ---------- main calculation ---------- */
      function blank(){
        ["rDose","rRate","rVial","rPred","respDeficit","respEarly","respLate","respExp","respPlasma"].forEach(function(id){ $(id).textContent = "–"; });
        $("rHero").innerHTML = '– <span class="u">mL total</span>';
        $("heroLbl").textContent = "Volume to give";
        $("rDoseU").textContent = "g total";
        $("rRateU").textContent = "mL/hr";
        $("rVialLbl").textContent = "Vials / bottles";
        $("rVialU").textContent = "to draw from";
        $("rPredU").textContent = (state.unit === "gdl" ? "g/dL" : "g/L") + " (estimate)";
        $("rowRemain").hidden = true;
        $("working").textContent = "";
        $("alerts").innerHTML = "";
        lastSummary = "";
      }

      var lastSummary = "";
      function calc(){
        var p = product();

        // slider progress + readout
        var gkgIn = $("gkg"), gkgV = parseFloat(gkgIn.value);
        var prog = (gkgV - parseFloat(gkgIn.min)) / (parseFloat(gkgIn.max) - parseFloat(gkgIn.min)) * 100;
        gkgIn.style.setProperty("--adc-range-progress", prog + "%");
        $("gkgOut").textContent = fmt(gkgV, 2);

        // weight
        var wtRaw = parseFloat($("wt").value);
        var wt = state.wunit === "lb" ? wtRaw / LB_PER_KG : wtRaw;

        // albumin values in g/dL
        var albRaw = parseFloat($("alb").value);
        var alb = state.unit === "gl" ? albRaw / 10 : albRaw;
        var tgtRaw = parseFloat($("target").value);
        var tgt = state.unit === "gl" ? tgtRaw / 10 : tgtRaw;

        // interpretation panel
        var ip = interpret(alb, state.species), ie = $("interp");
        ie.innerHTML = ip
          ? '<div class="interp ' + ip.cls + '"><span class="badge ' + ip.badgeCls + '">' + ip.badge + '</span><div class="lead">' + ip.lead + '</div><div class="desc">' + ip.desc + '</div><div class="desc ref">' + ip.ref + '</div></div>'
          : '<div class="interp mut"><div class="desc">Enter the current serum albumin for automatic interpretation.</div></div>';

        var dur = parseFloat($("dur").value);
        var needTgt = state.mode === "deficit";
        if(isNaN(wt) || wt <= 0 || isNaN(dur) || dur <= 0 || !isFinite(p.gml) || (needTgt && (isNaN(alb) || isNaN(tgt))) || (!needTgt && isNaN(gkgV))){
          blank(); return;
        }

        // dose
        var deficitG = 0, doseG, capped = false, gkgReq;
        if(needTgt){
          deficitG = Math.max(0, 10 * (tgt - alb) * wt * DIST);
          doseG = deficitG;
        } else {
          doseG = gkgV * wt;
        }
        gkgReq = doseG / wt;
        if(state.cap === "on" && gkgReq > CAP_GKG + 1e-9){ doseG = CAP_GKG * wt; capped = true; }
        var gkgAct = doseG / wt;

        var volMl = doseG / p.gml;
        var rate = volMl / dur, mlkghr = rate / wt, mlmin = rate / 60, mlkgTot = volMl / wt;

        // predicted response (estimates)
        var earlyLo = gkgAct / EARLY_LO, earlyHi = gkgAct / EARLY_HI, late = gkgAct / (10 * DIST);
        var plasmaMl = doseG / PLASMA_ALB;
        var hyper = p.gml >= 0.16;
        var expansion = hyper ? volMl * (p.gml / 0.05) : NaN;

        // ---- outputs ----
        $("heroLbl").textContent = p.name + " to give" + (capped ? " today (capped)" : "");
        $("rHero").innerHTML = (doseG > 0 ? fmtSmart(volMl) : "0") + ' <span class="u">mL total' + (doseG > 0 ? " · " + fmt(mlkgTot, mlkgTot < 10 ? 2 : 1) + " mL/kg" : "") + '</span>';
        $("rDose").textContent = fmt(doseG, doseG < 10 ? 2 : 1);
        $("rDoseU").textContent = "g total · " + fmt(gkgAct, 2) + " g/kg";
        $("rRate").textContent = fmtSmart(rate);
        $("rRateU").textContent = "mL/hr · " + fmt(mlkghr, 2) + " mL/kg/hr · " + fmt(mlmin, 2) + " mL/min";

        if(p.canine){
          var vials = Math.ceil(doseG / p.vialG - 1e-9);
          var perVialMl = p.vialG / p.gml;
          $("rVialLbl").textContent = "5 g vials";
          $("rVial").textContent = doseG > 0 ? String(vials) : "0";
          $("rVialU").textContent = doseG > 0
            ? (p.diluent ? "add " + p.diluent + " mL diluent per vial ≈ " + fmt(perVialMl, 0) + " mL at " + fmt(p.gml*100, 0) + "%" : "each ≈ " + fmt(perVialMl, 0) + " mL at " + fmt(p.gml*100, 0) + "%") + " · draw up " + fmtSmart(volMl) + " mL"
            : "vials";
        } else {
          var bottle = parseFloat($("bottle").value);
          $("rVialLbl").textContent = "Bottles";
          if(isFinite(bottle) && bottle > 0 && doseG > 0){
            $("rVial").textContent = String(Math.ceil(volMl / bottle - 1e-9));
            $("rVialU").textContent = "× " + fmt(bottle, 0) + " mL bottles (" + fmt(bottle * p.gml, 1) + " g each)";
          } else {
            $("rVial").textContent = doseG > 0 ? "–" : "0";
            $("rVialU").textContent = doseG > 0 ? "enter bottle volume" : "bottles";
          }
        }

        var uName = state.unit === "gdl" ? "g/dL" : "g/L";
        if(!isNaN(alb) && doseG > 0){
          $("rPred").textContent = inUnit(alb + earlyLo).replace(/ g\/(dL|L)$/, "") + " to " + inUnit(alb + earlyHi).replace(/ g\/(dL|L)$/, "");
          $("rPredU").textContent = uName + ", estimate at 2 to 6 h · settles toward " + inUnit(alb + late) + " by 24 to 48 h";
        } else if(!isNaN(alb)) {
          $("rPred").textContent = inUnit(alb).replace(/ g\/(dL|L)$/, "");
          $("rPredU").textContent = uName + " · no dose at this target";
        } else {
          $("rPred").textContent = "–";
          $("rPredU").textContent = "enter current albumin";
        }

        // response block
        $("respDeficit").textContent = needTgt ? fmt(deficitG, deficitG < 10 ? 2 : 1) + " g (" + fmt(gkgReq, 2) + " g/kg)" : "–";
        $("rowRemain").hidden = !capped;
        $("respRemain").textContent = capped ? fmt(deficitG - doseG, 1) + " g (" + fmtSmart((deficitG - doseG) / p.gml) + " mL of this product)" : "–";
        $("respEarly").textContent = doseG > 0 ? "+" + riseInUnit(earlyLo) + " to +" + riseInUnit(earlyHi) : "–";
        $("respLate").textContent = doseG > 0 ? "+" + riseInUnit(late) : "–";
        $("rowExp").hidden = !hyper;
        $("respExp").textContent = hyper && doseG > 0 ? "≈ " + fmtSmart(expansion) + " mL (about " + fmtX(p.gml/0.05) + "× the infused volume)" : "–";
        $("respPlasma").textContent = doseG > 0 ? "≈ " + fmtSmart(plasmaMl) + " mL (" + fmt(plasmaMl / wt, 0) + " mL/kg)" : "–";
        var basis = " Early rise assumes about +0.4 to +0.8 g/dL per 1 g/kg (healthy dogs given 1 g/kg rose a mean of 0.48 g/dL at 2 h); the redistributed value uses the 0.3 L/kg formula. Ongoing losses blunt both.";
        $("working").innerHTML = needTgt
          ? "Working: 10 × (" + fmt(tgt,1) + " − " + fmt(alb,1) + " g/dL) × " + fmt(wt,1) + " kg × 0.3 = <b>" + fmt(deficitG, deficitG < 10 ? 2 : 1) + " g</b>" + (capped ? ", capped to 2 g/kg = <b>" + fmt(doseG,1) + " g</b>" : "") + " ÷ " + fmt(p.gml*1000,0) + " mg/mL = <b>" + fmtSmart(volMl) + " mL</b>." + basis
          : "Working: " + fmt(gkgV,2) + " g/kg × " + fmt(wt,1) + " kg = <b>" + fmt(doseG, doseG < 10 ? 2 : 1) + " g</b> ÷ " + fmt(p.gml*1000,0) + " mg/mL = <b>" + fmtSmart(volMl) + " mL</b>." + basis;

        // ---- alerts ----
        var al = [];
        var isCat = state.species === "cat";
        var wtKgRaw = wt;
        if(wtKgRaw > 100){
          al.push({ c:"w", t:"<b>Check the weight.</b> " + fmt(wtKgRaw,1) + " kg is unusually high; confirm the value and the kg / lb toggle before drawing up." });
        } else if(wtKgRaw < 0.3){
          al.push({ c:"w", t:"<b>Check the weight.</b> " + fmt(wtKgRaw,2) + " kg is unusually low; confirm the value and the kg / lb toggle before drawing up." });
        }
        if(needTgt && !isNaN(tgt) && tgt > 3.0){
          al.push({ c:"i", t:"<b>High target.</b> Targets above 2.5 to 3.0 g/dL (25 to 30 g/L) are rarely needed: the aim is adequate oncotic pressure, not a normal albumin. Consider 2.0 to 2.5 g/dL." });
        }

        if(needTgt && deficitG <= 0){
          al.push({ c:"i", t:"<b>No deficit at this target.</b> Current albumin (" + inUnit(alb) + ") is at or above the target (" + inUnit(tgt) + "). Raise the target only if there is a clinical reason, or switch to dose per kg." });
        }
        if(p.human && state.prior === "yes"){
          al.push({ c:"d", t:"<b>Previous human albumin exposure: do not give human albumin.</b> Re-exposure risks anaphylaxis and immune-complex disease. Use canine albumin or plasma instead." });
        }
        if(p.canine && state.prior === "yes"){
          al.push({ c:"i", t:"<b>Previous human albumin exposure noted.</b> Canine albumin is the appropriate choice; avoid any human albumin product for this patient." });
        }
        if(!capped && gkgAct > CAP_GKG + 1e-9){
          al.push({ c:"d", t:"<b>Dose exceeds 2 g/kg per day</b> (" + fmt(gkgAct,2) + " g/kg). Published practice is 0.5 to 1 g/kg, up to about 2 g/kg per day. Split the deficit over several days and recheck albumin and COP between doses." });
        }
        if(capped){
          al.push({ c:"w", t:"<b>Deficit exceeds 2 g/kg.</b> The formula gives " + fmt(deficitG, 1) + " g (" + fmt(gkgReq,2) + " g/kg); today's dose is capped at 2 g/kg = " + fmt(doseG,1) + " g. Recheck albumin and COP, then give the remainder over the following days if still needed." });
        }
        if(hyper && mlkghr > HSA_CRI_MAX + 1e-9 && p.human && doseG > 0){
          al.push({ c:"w", t:"<b>Rate is " + fmt(mlkghr,2) + " mL/kg/hr.</b> Published CRI rates for hyperoncotic (25%) human albumin are 0.7 to 1.7 mL/kg/hr; 2 mL/kg over 2 h has been used for hypotension. Lengthen the infusion unless treating refractory hypotension." });
        }
        if(p.canine && mlmin > CSA_MLMIN + 1e-9 && doseG > 0){
          al.push({ c:"w", t:"<b>Rate is " + fmt(mlmin,2) + " mL/min.</b> The canine albumin insert suggests no more than about 1 mL/min for normovolaemic patients; 450 to 800 mg/kg over 4 to 8 h is described for hypotensive or hypovolaemic patients. Slow the infusion if the patient is normovolaemic." });
        }
        if(mlkgTot > VOL_WARN && doseG > 0){
          al.push({ c:"w", t:"<b>Large volume for this patient</b> (" + fmt(mlkgTot,1) + " mL/kg). Reduce concurrent crystalloids, consider a more concentrated product or a longer infusion, and monitor for overload." });
        }
        if(state.volsens === "yes" && doseG > 0){
          al.push({ c:"d", t:"<b>Volume-sensitive patient.</b> Albumin, especially hyperoncotic solutions, can precipitate pulmonary oedema. Use the smallest effective dose, run it as a slow CRI (12 to 24 h), stop concurrent crystalloids and monitor respiratory rate and effort, lung sounds and blood pressure throughout." });
        }
        if(!isNaN(alb) && doseG > 0 && alb + earlyLo > OVERSHOOT){
          al.push({ c:"w", t:"<b>Possible overshoot.</b> The estimated early albumin is " + inUnit(alb + earlyLo) + " or higher, above " + inUnit(OVERSHOOT) + ". Consider giving part of the dose, rechecking albumin and COP, then deciding on the remainder." });
        }
        if(hyper && doseG > 0){
          al.push({ c:"w", t:"<b>Hyperoncotic solution.</b> Expect plasma expansion of roughly " + fmtX(p.gml/0.05) + "× the infused volume (≈ " + fmtSmart(expansion) + " mL). Reduce crystalloid rates while it runs and watch for tachypnoea, crackles and hypertension." });
        }
        if(p.human && state.prior !== "yes" && doseG > 0){
          al.push({ c:"w", t: isCat
            ? "<b>Human albumin in cats: limited evidence.</b> Reactions (fever, gut signs, tremors, facial swelling) were reported in a proportion of cats given 5% or 25% human albumin. Give slowly with transfusion-style monitoring, single course only, and never re-expose."
            : "<b>Human albumin is a foreign protein.</b> Acute (type I) and delayed (type III, 5 to 14 days: vasculitis, polyarthritis, oedema, kidney injury) reactions occur, more often in healthy or less critical dogs. Complete the course within about 3 to 5 days, never re-expose this patient, and prefer canine albumin where available." });
        }
        if(p.canine && doseG > 0){
          al.push({ c: isCat ? "w" : "i", t: isCat
            ? "<b>Canine albumin in a cat is a xenotransfusion.</b> A small case series (0.7 to 2 g/kg) reported oncotic support with one suspected circulatory overload. Off-label: give slowly, monitor as for a transfusion, and be ready to stop."
            : "<b>Species-specific product.</b> Reactions are uncommon (vomiting, urticaria, fever; hypotension and vasodilation possible). Healthy dogs tolerated three 1 g/kg doses of 16% over 2 h (days 1, 2 and 14) with a mean albumin rise of 0.48 g/dL at 2 h. Follow the manufacturer's diluent and reconstitution instructions and use within the stated time." });
        }
        if(state.product === "custom" && doseG > 0){
          al.push({ c:"i", t:"<b>Custom concentration.</b> Volumes above assume " + fmt(p.gml*1000,0) + " mg/mL. Confirm the label concentration and, for reconstituted products, the final volume." });
        }
        $("alerts").innerHTML = al.map(function(a){
          return '<div class="alert ' + a.c + '"><span class="ic">' + (a.c === "d" ? "!" : a.c === "w" ? "⚠" : "i") + '</span><span>' + a.t + '</span></div>';
        }).join("");

        // plain-text summary for the Copy button
        var lines = [];
        lines.push("ALBUMIN DOSE (ASK A VET clinical calculator)");
        lines.push((isCat ? "Cat" : "Dog") + ", " + fmt(wt,1) + " kg" + (state.wunit === "lb" ? " (" + fmt(wtRaw,1) + " lb)" : ""));
        if(!isNaN(alb)) lines.push("Current albumin " + albDisplay(alb));
        lines.push(needTgt
          ? "Target " + inUnit(tgt) + "; deficit 10 x (" + fmt(tgt,1) + " - " + fmt(alb,1) + ") x " + fmt(wt,1) + " x 0.3 = " + fmt(deficitG, deficitG < 10 ? 2 : 1) + " g" + (capped ? " (capped at 2 g/kg for today)" : "")
          : "Dose " + fmt(gkgV,2) + " g/kg");
        lines.push("Product: " + p.name + " (" + fmt(p.gml*1000,0) + " mg/mL)");
        lines.push("Give: " + fmt(doseG, doseG < 10 ? 2 : 1) + " g = " + fmtSmart(volMl) + " mL (" + fmt(gkgAct,2) + " g/kg, " + fmt(mlkgTot, mlkgTot < 10 ? 2 : 1) + " mL/kg) over " + fmt(dur, dur % 1 ? 1 : 0) + " h = " + fmtSmart(rate) + " mL/hr (" + fmt(mlkghr,2) + " mL/kg/hr)");
        if(p.canine) lines.push("Vials: " + Math.ceil(doseG / p.vialG - 1e-9) + " x 5 g");
        if(!isNaN(alb) && doseG > 0) lines.push("Predicted albumin at 2 to 6 h: " + inUnit(alb + earlyLo) + " to " + inUnit(alb + earlyHi) + " (estimate); after redistribution about " + inUnit(alb + late));
        if(capped) lines.push("Remaining deficit after today: " + fmt(deficitG - doseG,1) + " g");
        var flags = al.filter(function(a){ return a.c === "d" || a.c === "w"; }).map(function(a){ return "- " + a.t.replace(/<[^>]+>/g, ""); });
        if(flags.length) lines.push("Checks:"); lines = lines.concat(flags);
        lines.push("Verify concentration against the vial. Decision-support only; responsibility rests with the prescribing veterinarian.");
        lastSummary = lines.join("\n");
      }

      /* ---------- reset ---------- */
      function resetAll(){
        state.species = "dog"; setSeg("species","dog");
        state.wunit = "kg"; setSeg("wunit","kg"); $("wt").value = 20;
        state.unit = "gdl"; setSeg("unit","gdl"); $("alb").value = 1.5; $("alb").step = "0.1";
        $("target").value = 2.0; $("target").step = "0.1"; $("targetUnit").textContent = "g/dL";
        state.mode = "deficit"; setSeg("mode","deficit");
        $("gkg").value = 0.8;
        state.product = PRODUCTS[root.dataset.defaultProduct] ? root.dataset.defaultProduct : "h25"; $("product").value = state.product;
        $("cpct").value = 10; state.csrc = "human"; setSeg("csrc","human");
        state.prior = "no"; setSeg("prior","no");
        state.volsens = "no"; setSeg("volsens","no");
        state.cap = "on"; setSeg("cap","on");
        dirty.dur = false; dirty.bottle = false;
        syncMode(); syncProduct(true); calc();
      }
      root.querySelectorAll("[data-adc-reset]").forEach(function(b){ b.addEventListener("click", resetAll); });
      root.querySelectorAll("[data-adc-copy]").forEach(function(b){
        b.addEventListener("click", function(){
          if(!lastSummary){ return; }
          var done = function(ok){
            var label = b.textContent;
            b.textContent = ok ? "Copied" : "Copy failed";
            b.classList.toggle("copied", !!ok);
            setTimeout(function(){ b.textContent = "Copy summary"; b.classList.remove("copied"); }, 1600);
          };
          if(navigator.clipboard && navigator.clipboard.writeText){
            navigator.clipboard.writeText(lastSummary).then(function(){ done(true); }, function(){ done(fallbackCopy(lastSummary)); });
          } else {
            done(fallbackCopy(lastSummary));
          }
        });
      });
      function fallbackCopy(text){
        try{
          var ta = document.createElement("textarea");
          ta.value = text; ta.setAttribute("readonly",""); ta.style.position = "fixed"; ta.style.opacity = "0";
          document.body.appendChild(ta); ta.select();
          var ok = document.execCommand("copy");
          document.body.removeChild(ta);
          return ok;
        }catch(e){ return false; }
      }

      /* ---------- init ---------- */
      setSeg("species", state.species);
      setSeg("unit", state.unit);
      if(state.unit === "gl"){ $("alb").value = 15; $("target").value = 20; $("alb").step = "1"; $("target").step = "1"; $("targetUnit").textContent = "g/L"; }
      $("product").value = state.product;
      syncMode(); syncProduct(true); calc();
    }
  }
  if(!customElements.get("s-albumin-dose-calculator")) customElements.define("s-albumin-dose-calculator", AlbuminDoseCalculator);
})();
